import { useEffect, useMemo, useState } from 'react'

import { InjectionTokens } from '@/controller/tokens'

import { ADHP } from '@/domain/models'
import { FuelData } from '@/domain/protocols/Fuel'

import { IAeroInfoRepository } from '@/data/AeroInfoRepository/IAeroInfoRepository'
import { IFuelRepository } from '@/data/FuelRepository/IFuelRepository'

import { MapFuel } from '../components/MapFuel/MapFuel'
import { FeatureInfoStateMutator } from '../states/FeatureInfoState'
import { FuelState, FuelStateProps } from '../states/FuelState'
import { ScreenElementsStateMutator } from '../states/ScreenElementsState'
import { stackIds } from './MapStackPresenter'

import { MetricsEvents } from '@/constants/metricsEvents'
import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { sendMetrics } from '@/presentation/utils/sendMetrics'

interface FuelAerodromeSummary {
  adhpCode: string
  providers: FuelData[]
}

const FUEL_REFRESH_INTERVAL_MS = 10 * 60 * 1000

function MapFuelPresenter() {
  const fuelState = useBehaviorSubject<FuelStateProps>(FuelState)
  const fuelRepository = useInjection<IFuelRepository>(InjectionTokens.FuelRepository)
  const aeroInfoRepository = useInjection<IAeroInfoRepository>(InjectionTokens.AeroInfoRepository)
  const [refreshTick, setRefreshTick] = useState(0)

  useEffect(() => {
    if (!fuelState.layer) return

    let unmounted = false

    const refreshFuelData = async () => {
      const result = await fuelRepository.refresh()
      if (unmounted || result.isFailure) return
      setRefreshTick((prev) => prev + 1)
    }

    void refreshFuelData()

    const interval = setInterval(() => {
      void refreshFuelData()
    }, FUEL_REFRESH_INTERVAL_MS)

    return () => {
      unmounted = true
      clearInterval(interval)
    }
  }, [fuelState.layer, fuelRepository])

  const summaries = useMemo<FuelAerodromeSummary[]>(() => {
    if (!fuelState.layer) return []

    const allFuelResult = fuelRepository.getAllFuel()
    if (allFuelResult.isFailure) return []

    const groupedByIcao = new Map<string, FuelData[]>()
    for (const provider of allFuelResult.getValue()) {
      const icao = provider.adhpCode?.trim().toUpperCase()
      if (!icao) continue

      const current = groupedByIcao.get(icao) ?? []
      current.push(provider)
      groupedByIcao.set(icao, current)
    }

    return Array.from(groupedByIcao.entries()).map(([adhpCode, providers]) => ({
      adhpCode,
      providers
    }))
  }, [fuelState.layer, fuelRepository, refreshTick])

  const adhps = useMemo<ADHP[]>(() => {
    const result = aeroInfoRepository.getAllADHPs()
    return result.isSuccess ? result.getValue() : []
  }, [aeroInfoRepository])

  const handleLabelClick = (adhpCode: string) => {
    if (!fuelState.layer) return

    const fuelByIcao = fuelRepository.getFuelByIcao(adhpCode, fuelState.layer)
    if (fuelByIcao.isFailure) return

    const providers = fuelByIcao.getValue()
    const adhp = adhps.find((item) => item.code === adhpCode)
    const title = adhp ? `${adhp.code} - ${adhp.name}` : adhpCode

    // Emitido só quando o painel realmente abre (os guards acima já filtraram as falhas).
    sendMetrics(MetricsEvents.FUEL_MAP_POINT_CLICKED, {
      adhpCode,
      fuelType: fuelState.layer,
      providerCount: providers.length
    })

    FeatureInfoStateMutator.showFuel(title, adhpCode, providers)
    ScreenElementsStateMutator.setFeatureInfoPanel(true)
  }

  if (!fuelState.layer) return null

  return (
    <MapFuel
      summaries={summaries}
      adhps={adhps}
      activeLayer={fuelState.layer}
      beforeLayerId={stackIds.fuel}
      onLabelClick={handleLabelClick}
    />
  )
}

export { MapFuelPresenter }
